/* eslint-disable */
import { useState, useEffect } from 'react';
import { useDispatch } from 'store';

// material-ui
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormHelperText,
    Box,
} from '@mui/material';

// project imports
import InputLabel from 'ui-component/extended/Form/InputLabel';
import { useCurrentUser } from 'hooks/useUser.hooks';
import { openSnackbar } from 'store/slices/snackbar';
import axios from 'utils/axios';

interface ChangeEmailDialogProps {
    open: boolean;
    onClose: () => void;
}

const ChangeEmailDialog = ({ open, onClose }: ChangeEmailDialogProps) => {
    const currentUserState = useCurrentUser(true);
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [originalEmail, setOriginalEmail] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [loading, setLoading] = useState(false);

    // Get the user's current email when dialog opens
    useEffect(() => {
        if (open && currentUserState.ready) {
            const userEmail = currentUserState.user.email ?? '';
            setEmail(userEmail);
            setOriginalEmail(userEmail);
            setEmailSent(false);
        }
    }, [open, currentUserState.ready]);

    const handleEmailChange = (e: any) => {
        setEmail(e.target.value);
        setEmailSent(false); // Reset sent state when email changes
    };

    const handleSendEmailVerification = async () => {
        const newEmail = email.trim();

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmail)) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Invalid email format. Please provide a valid email address.',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: false
                })
            );
            return;
        }

        // Check if same as current email
        if (newEmail.toLowerCase() === originalEmail.toLowerCase()) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'The new email address is the same as your current email address.',
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: false
                })
            );
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post('/berryAPI/user/requestEmailChange', {
                newEmail: newEmail
            });

            // Mark as sent
            setEmailSent(true);

            dispatch(
                openSnackbar({
                    open: true,
                    message: response.data.message || 'Verification email sent to ' + newEmail + '. Please check your inbox.',
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: false
                })
            );

            // Close dialog after successful send
            setTimeout(() => {
                onClose();
            }, 1500);

        } catch (error: any) {
            const errorMessage = error.response?.data?.error || 'Failed to send verification email. Please try again later.';
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage,
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: false
                })
            );
        } finally {
            setLoading(false);
        }
    };

    const emailHasChanged = email.toLowerCase().trim() !== originalEmail.toLowerCase().trim();

    if (!currentUserState.ready) {
        return null;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Change Email Address</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    <InputLabel>Current Email Address</InputLabel>
                    <TextField
                        fullWidth
                        type="email"
                        value={originalEmail}
                        disabled
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <InputLabel>New Email Address</InputLabel>
                    <TextField
                        fullWidth
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        error={emailHasChanged && !emailSent}
                        sx={{ mt: 1 }}
                    />
                    {emailHasChanged && !emailSent && (
                        <FormHelperText error sx={{ mt: 1 }}>
                            Email will not be changed until you verify the new address. Click "Send Verification Email" below.
                        </FormHelperText>
                    )}
                    {emailSent && (
                        <FormHelperText sx={{ mt: 1, color: 'success.main' }}>
                            Verification email sent! Please check your inbox and click the verification link to complete the email change.
                        </FormHelperText>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSendEmailVerification}
                    disabled={!emailHasChanged || loading}
                    color="primary"
                >
                    {emailSent ? 'Resend Verification Email' : 'Send Verification Email'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeEmailDialog;

